import { EventSourcePolyfill } from 'event-source-polyfill';
import { API_BASE } from '../config/vars';
import { getAuth0Client } from './auth0';
import { getBrowserLocalDateTime } from '../utils/dates';

/**
 * Open an SSE stream for a report generation run using EventSource polyfill.
 * Backend is now GET /v1/ai/reports/:runId?message=...
 * Only forwards events of type 'custom-event' and 'error'.
 * Returns an object with a cancel() function.
 */
export async function streamGenerate(
  runId,
  message,
  handlers = {},
  options = {},
) {
  const { timeoutMs, reportId } = options || {};
  const { onCustomEvent, onError, onClose } = handlers || {};

  const client = getAuth0Client && getAuth0Client();
  const token =
    client && client.getTokenSilently ? await client.getTokenSilently() : '';

  const base = String(API_BASE || '').replace(/\/$/, '');
  let url = `${base}/v1/ai/reports/${encodeURIComponent(
    runId,
  )}?message=${encodeURIComponent(message || '')}`;
  if (reportId) {
    url += `&reportId=${encodeURIComponent(reportId)}`;
  }
  const browserLocalDateTime = getBrowserLocalDateTime();
  url += `&browserLocalDateTime=${encodeURIComponent(browserLocalDateTime)}`;

  let closed = false;
  let timeoutId = null;
  let started = false; // whether we've received any server event

  // Create EventSource with Authorization header via polyfill
  const es = new EventSourcePolyfill(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    // do not auto-retry on error for request/response style streams
    // (the polyfill will try by default; we will close on error below)
  });

  const cleanup = () => {
    if (!closed) {
      closed = true;
      try {
        es.close();
      } catch (e) {}
      if (timeoutId) clearTimeout(timeoutId);
      if (typeof onClose === 'function') onClose();
    }
  };

  if (typeof timeoutMs === 'number' && timeoutMs > 0) {
    timeoutId = setTimeout(() => {
      cleanup();
    }, timeoutMs);
  }

  es.addEventListener('custom-event', (e) => {
    try {
      const payload = JSON.parse((e && e.data) || '{}');
      started = true;
      if (typeof onCustomEvent === 'function') onCustomEvent(payload);
    } catch (err) {
      // ignore parse errors
    }
  });

  // Application-level error sent by server; show modal and close stream
  es.addEventListener('server-error', (e) => {
    let message = 'An error occurred';
    try {
      if (e && typeof e.data === 'string' && e.data) {
        const payload = JSON.parse(e.data);
        if (payload && payload.message) message = String(payload.message);
      }
    } catch (_) {}
    if (typeof onError === 'function') onError(message);
    cleanup();
  });

  // Explicit close event from server – close stream and notify caller
  es.addEventListener('close', (e) => {
    // Check if this is a workflow-finished-error and treat it as an error
    try {
      if (e && typeof e.data === 'string' && e.data) {
        const payload = JSON.parse(e.data);
        if (payload && payload.reason === 'workflow-finished-error') {
          if (typeof onError === 'function') onError('An error occurred');
          cleanup();
          return;
        }
      }
    } catch (_) {}
    cleanup();
  });

  es.addEventListener('error', (e) => {
    // Some servers emit a custom SSE 'error' event with a JSON payload in e.data
    // Otherwise, 'error' may also be dispatched on normal close/end-of-stream.
    // Treat this as transport-level. If it’s just a normal close, don’t show an error.
    // If we've begun receiving events and the source is now closed, treat as normal close
    // to avoid a spurious error dialog after a successful stream completion.
    const CLOSED = 2;
    if (started && es && typeof es.readyState === 'number' && es.readyState === CLOSED) {
      cleanup();
      return;
    }

    // If we never started, surface as a connection error; if we started, show a generic transport error.
    if (typeof onError === 'function') {
      if (!started) onError('Failed to connect to stream');
      else onError('Stream connection error');
    }
    cleanup();
  });

  return {
    cancel: () => cleanup(),
  };
}
